<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { toTitleCase } from "@/utils/stringUtils";
import { usePlanStore } from "@/stores/usePlanStore";

// Componentes Modais
import AddPlanForm from "./AddPlanForm.vue";
import EditPlanForm from "./EditPlanForm.vue";
import DeletePlanForm from "./DeletePlanForm.vue";
import PlanCardModal from "./PlanCardModal.vue";
import BatchDeletePlanForm from "./BatchDeletePlanForm.vue";
import SearchPlanInput from "./SearchPlanInput.vue";
import PlanHelpModal from "./PlanHelpModal.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const planStore = usePlanStore();

// Estados dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCardModal = ref(false);
const selectedPlanCardId = ref(null);

// Modal de ajuda
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ loading visual da tabela
const isLoadingTable = ref(false);

// Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO (igual TextForm.vue) — 3000ms
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

// helper simples (por enquanto tudo é editável)
function canEdit() {
  return true;
}

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = planStore.plans
      .filter((p) => canEdit(p))
      .map((p) => p.planId);

    if (editableIdsOnPage.length === 0) return false;

    return editableIdsOnPage.every((id) =>
      planStore.selectedPlanIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = planStore.plans
      .filter((p) => canEdit(p))
      .map((p) => p.planId);

    const allIdsOnPage = planStore.plans.map((p) => p.planId);

    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!planStore.selectedPlanIds.includes(id)) {
          planStore.selectedPlanIds.push(id);
        }
      });
    } else {
      planStore.selectedPlanIds = planStore.selectedPlanIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

const anyEditableOnPage = computed(() =>
  planStore.plans.some((p) => canEdit(p))
);

const canBatchActions = computed(() => {
  return (
    Array.isArray(planStore.selectedPlanIds) &&
    planStore.selectedPlanIds.length > 0
  );
});

function toggleRowDropdown(planId) {
  openDropdownId.value = openDropdownId.value === planId ? null : planId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const plan = planStore.plans[index];
  selectedPlanCardId.value = plan.planId;
  planStore.setIdPlan(plan.planId);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  planStore.toggleSelection(id);
}

// Fetch de dados
async function fetchPlansPaged() {
  try {
    isLoadingTable.value = true;
    await planStore.fetchPlansPaged(
      currentPage.value,
      pageSize.value,
      planStore.searchTerm
    );
  } catch (error) {
    console.error("❌ Erro ao buscar planos paginados:", error?.message);
    showErrorToast("Erro ao carregar planos.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchPlansPaged);

function tryOpenBatchDeleteModal() {
  const selectedIds = planStore.selectedPlanIds;

  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhum plano selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }

  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ====================================================================
// ✅ Handler único pós-mudança (padrão TextForm.vue)
// ====================================================================
async function handlePlansChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) {
    planStore.selectedPlanIds = [];
  }

  if (successMessage) {
    showSuccessToast(successMessage);
  }

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchPlansPaged();
    return;
  }

  await fetchPlansPaged();
}
// ====================================================================

// Lógica para fechar dropdowns
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchPlansPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  planStore.searchTerm = "";
  planStore.currentPage = 1;
  planStore.shouldForcePageOne = true;

  planStore.selectedPlanIds = [];
  planStore.idPlan = null;
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedPlanCardId.value = null;

  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  showHelpModal.value = false;

  isLoadingTable.value = false;
});

function indexSelector(index) {
  const plan = planStore.plans[index];
  planStore.setIdPlan(plan.planId);
}

// Funções para os toggles
async function handleToggleExport(planId) {
  try {
    await planStore.toggleCanExport(planId);
  } catch (error) {
    console.error("Falha ao alternar CanExport:", error);
    await fetchPlansPaged();
  }
}

async function handleToggleDashboard(planId) {
  try {
    await planStore.toggleHasDashboard(planId);
  } catch (error) {
    console.error("Falha ao alternar HasDashboard:", error);
    await fetchPlansPaged();
  }
}

async function handleToggleActive(planId) {
  try {
    await planStore.toggleIsActive(planId);
  } catch (error) {
    console.error("Falha ao alternar IsActive:", error);
    await fetchPlansPaged();
  }
}

async function handleTogglePublic(planId) {
  try {
    await planStore.toggleIsPublic(planId);
  } catch (error) {
    console.error("Falha ao alternar IsPublic:", error);
    await fetchPlansPaged();
  }
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Planos</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Esta seção permite gerenciar os planos de assinatura do sistema,
            incluindo limites de uso, recursos disponíveis e ciclo de cobrança.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchPlanInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Criar Plano
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 68px">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 15%">Nome</th>
              <th style="width: 8%">Tipo</th>
              <th style="width: 8%">Gerenciados</th>
              <th style="width: 10%">Preço</th>
              <th style="width: 10%">Ciclo</th>
              <th style="width: 8%">Usuários</th>
              <th style="width: 8%">Textos</th>
              <th style="width: 8%">Notas</th>
              <th style="width: 8%">Exportar</th>
              <th style="width: 8%">Dashboard</th>
              <th style="width: 8%">Ativo</th>
              <th style="width: 8%">Público</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(plan, index) in !isLoadingTable ? planStore.plans : []"
              :key="plan.planId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td @click.stop>
                <input
                  :class="{ 'disabled-item': !canEdit(plan) }"
                  type="checkbox"
                  :value="plan.planId"
                  :checked="planStore.selectedPlanIds?.includes(plan.planId)"
                  @change="toggleSelection(plan.planId)"
                  class="checkbox-style"
                  :disabled="!canEdit(plan)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(plan.planId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === plan.planId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li @click="handleEdit(index)">
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>
                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>
                    <li @click="handleDelete(index)" class="danger-item">
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="plan.planId">{{ plan.planId }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="plan.name">{{ toTitleCase(plan.name) }}</span>
              </td>

              <td>
                <span>{{ plan.usageType }}</span>
              </td>
              <td>
                <span>{{ plan.maxManagedAccounts }}</span>
              </td>
              <td>
                <span>
                  {{
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(plan.price)
                  }}
                </span>
              </td>
              <td>
                <span>{{ plan.billingCycle }}</span>
              </td>
              <td>
                <span>{{ plan.maxGroupUser }}</span>
              </td>
              <td>
                <span>{{ plan.maxTextCharacters }}</span>
              </td>
              <td>
                <span>{{ plan.maxNoteCharacters }}</span>
              </td>

              <td @click.stop>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="plan.canExport"
                    @change="handleToggleExport(plan.planId)"
                  />
                  <span class="slider"></span>
                </label>
              </td>

              <td @click.stop>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="plan.hasDashboard"
                    @change="handleToggleDashboard(plan.planId)"
                  />
                  <span class="slider"></span>
                </label>
              </td>

              <td @click.stop>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="plan.isActive"
                    @change="handleToggleActive(plan.planId)"
                  />
                  <span class="slider"></span>
                </label>
              </td>

              <td @click.stop>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="plan.isPublic"
                    @change="handleTogglePublic(plan.planId)"
                  />
                  <span class="slider"></span>
                </label>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="15" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando planos...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(planStore.totalCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="15" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum plano encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (planStore.totalCount ?? 0) > 0"
          class="plan-pagination-row"
        >
          <div class="plan-pagination-left">
            <span class="plan-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="planStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="planStore.totalCount ?? 0"
            :total-pages="planStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddPlanForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="handlePlansChanged({ goToFirstPage: true, closeAdd: true })"
    />

    <EditPlanForm
      v-if="showEditModal"
      :planId="planStore.idPlan"
      @close="showEditModal = false"
      @refresh="handlePlansChanged({ goToFirstPage: false, closeEdit: true })"
    />

    <PlanCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :plan-id="selectedPlanCardId"
      @close="showCardModal = false"
    />

    <DeletePlanForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="
        handlePlansChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeletePlanForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="
        handlePlansChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <PlanHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}

.action-cell-container {
  justify-content: center;
  align-items: center;
}

.main-table .cell-text-center {
  text-align: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 20px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--success);
}
input:checked + .slider:before {
  transform: translateX(20px);
}

.plan-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}

.plan-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.plan-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>